/**
 * Connectivity store — détecte à la fois la perte réseau et le serveur indisponible.
 *
 * - navigator.onLine → réseau client
 * - Échecs API consécutifs → serveur indisponible
 */

const listeners = new Set<() => void>();

let serverReachable = true;
let consecutiveFailures = 0;
const FAILURE_THRESHOLD = 2;

function notify() {
  for (const l of listeners) l();
}

export function reportApiSuccess(): void {
  if (!serverReachable || consecutiveFailures > 0) {
    consecutiveFailures = 0;
    serverReachable = true;
    notify();
  }
}

export function reportApiFailure(): void {
  consecutiveFailures++;
  if (consecutiveFailures >= FAILURE_THRESHOLD && serverReachable) {
    serverReachable = false;
    notify();
  }
}

export function isServerReachable(): boolean {
  return serverReachable;
}

export function isFullyOnline(): boolean {
  return navigator.onLine && serverReachable;
}

export function getConnectivityStatus(): "online" | "client-offline" | "server-unreachable" {
  if (!navigator.onLine) return "client-offline";
  if (!serverReachable) return "server-unreachable";
  return "online";
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// Sync result events
export interface SyncResult {
  succeeded: number;
  failed: number;
  rejected: number;
}

const syncListeners = new Set<(result: SyncResult) => void>();

export function onSyncComplete(listener: (result: SyncResult) => void): () => void {
  syncListeners.add(listener);
  return () => { syncListeners.delete(listener); };
}

export function emitSyncComplete(result: SyncResult): void {
  for (const l of syncListeners) l(result);
}
